import React, { Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import LocalWriteFailedBanner from './components/LocalWriteFailedBanner'
import { hydrateBulkStore } from './offline/bulkStore'
import { registerServiceWorker } from './offline/appShell/registerServiceWorker'
import { I18nProvider, resolveBrowserLocale, useT } from './i18n'
import './index.css'

if (import.meta.env.DEV) {
  // Make uncaught network errors debuggable (and help identify extension/content-script noise).
  window.addEventListener('unhandledrejection', (event) => {
    const reason: any = (event as any).reason
    const message = reason instanceof Error ? reason.message : String(reason)
    const name = reason?.name
    const stack = reason?.stack
    console.warn('[app] unhandledrejection', { name, message, stack, reason })
  })
}

// Loading component for better UX
const LoadingSpinner: React.FC = () => {
  const t = useT()
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '18px',
      color: '#6b7280'
    }}>
      <div style={{
        animation: 'spin 1s linear infinite',
        border: '3px solid #f3f4f6',
        borderTop: '3px solid #3b82f6',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        marginRight: '16px'
      }}></div>
      {t('app.loading')}
    </div>
  )
}

const locale = resolveBrowserLocale()

// Charter O-5 (Stage 5 delta A): keep the app's own files on the device so it
// opens with no signal, even after the browser was closed (production only).
registerServiceWorker()

// findings #75: load the IndexedDB-backed store for water / production tables
// before the first render, so every screen's synchronous read sees it. If
// IndexedDB is unavailable the app keeps using localStorage as before.
void hydrateBulkStore()
  .catch(() => false)
  .finally(() => {
    createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <ErrorBoundary>
          <I18nProvider locale={locale}>
            <BrowserRouter>
              <Suspense fallback={<LoadingSpinner />}>
                <App />
              </Suspense>
            </BrowserRouter>
            <LocalWriteFailedBanner />
          </I18nProvider>
        </ErrorBoundary>
      </React.StrictMode>
    )
  })
